import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "../queries/keys";
import { api } from "../runtime";
import type {
  AdminRole,
  DealerCredentials,
  ErrorStatus,
  FollowupCatchUpInput,
  FollowupSettingsValue,
  NewDealerUser,
  NewOrganization,
  OfferIntegrationDetail,
  OfferIntegrationInput,
  OfferIntegrationPatch,
  OrganizationPatch,
  SimulateInboundInput,
  StaffCredentials,
  StaffDetail,
  StoreDetail,
  StoreInput,
  StorePolicyPatch,
  StoreRole,
} from "../types";

/**
 * Admin actions. None are optimistic: the console shows the new state only after the backend
 * confirms. Invalidation names exactly the caches each action can change (and audit, since every
 * admin action is audited).
 */

export function useUpdateOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: OrganizationPatch) => api.organizations.update(id, patch),
    onSuccess: (org) => {
      qc.setQueryData(qk.organizations.detail(id), org);
      // The Offer API switch changes integration and store statuses too.
      for (const key of [qk.organizations.all, qk.integrations.all, qk.stores.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useSuspendOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.organizations.suspend(id, reason),
    onSuccess: (org) => {
      qc.setQueryData(qk.organizations.detail(id), org);
      for (const key of [qk.organizations.all, qk.stores.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useReactivateOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.organizations.reactivate(id),
    onSuccess: (org) => {
      qc.setQueryData(qk.organizations.detail(id), org);
      for (const key of [qk.organizations.all, qk.stores.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

function useUserMutation<V>(userId: string, fn: (v: V) => ReturnType<typeof api.users.get>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (user) => {
      qc.setQueryData(qk.users.detail(userId), user);
      for (const key of [qk.users.all, qk.stores.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export const useChangeUserRole = (userId: string) =>
  useUserMutation(userId, (v: { scope: { type: "organization" | "store"; id: string }; role: string }) => api.users.changeRole({ userId, ...v }));
export const useRemoveMembership = (userId: string) => useUserMutation(userId, (storeId: string) => api.users.removeMembership({ userId, storeId }));
export const useSuspendUser = (userId: string) => useUserMutation(userId, (reason: string) => api.users.suspend(userId, reason));
// biome-ignore lint/suspicious/noConfusingVoidType: mutate() takes no variables
export const useReactivateUser = (userId: string) => useUserMutation(userId, (_: void) => api.users.reactivate(userId));

function useQueueMutation<V, R>(queue: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of [qk.queues.all, qk.ai.all, qk.audit.all, qk.system.health()]) void qc.invalidateQueries({ queryKey: key });
    },
    meta: { queue },
  });
}

export const useRetryJob = (queue: string) => useQueueMutation(queue, (jobId: string) => api.queues.retryJob(queue, jobId));
export const useDiscardJob = (queue: string) => useQueueMutation(queue, (jobId: string) => api.queues.discardJob(queue, jobId));
// biome-ignore lint/suspicious/noConfusingVoidType: mutate() takes no variables
export const usePauseQueue = (queue: string) => useQueueMutation(queue, (_: void) => api.queues.pause(queue));
// biome-ignore lint/suspicious/noConfusingVoidType: mutate() takes no variables
export const useResumeQueue = (queue: string) => useQueueMutation(queue, (_: void) => api.queues.resume(queue));

export function useUpdateErrorStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: ErrorStatus }) => api.system.updateErrorStatus(v.id, v.status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.system.errors() });
      void qc.invalidateQueries({ queryKey: qk.audit.all });
    },
  });
}

export function useSignIn() {
  const _qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; password: string }) => api.session.signIn(v),
    // The login page reloads the console at the destination (browserLocation.replace).
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.session.signOut(), onSettled: () => qc.clear() });
}

// ── platform staff ───────────────────────────────────────────────────────
function useStaffMutation<V, R extends StaffDetail | StaffCredentials>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      const staff: StaffDetail = "temporaryPassword" in r ? (r as StaffCredentials).staff : (r as StaffDetail);
      qc.setQueryData(qk.staff.detail(staff.id), staff);
      for (const key of [qk.staff.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export const useCreateStaff = () => useStaffMutation((v: { name: string; email: string; role: AdminRole; slackUserId?: string | null }) => api.staff.create(v));
export const useUpdateStaff = (id: string) =>
  useStaffMutation((patch: { name?: string; role?: AdminRole; slackUserId?: string | null }) => api.staff.update(id, patch));
export const useDisableStaff = (id: string) => useStaffMutation((reason: string) => api.staff.disable(id, reason));
// biome-ignore lint/suspicious/noConfusingVoidType: mutate() takes no variables
export const useEnableStaff = (id: string) => useStaffMutation((_: void) => api.staff.enable(id));
// biome-ignore lint/suspicious/noConfusingVoidType: mutate() takes no variables
export const useResetStaffPassword = (id: string) => useStaffMutation((_: void) => api.staff.resetPassword(id));
// biome-ignore lint/suspicious/noConfusingVoidType: mutate() takes no variables
export const useRevokeStaffSessions = (id: string) => useStaffMutation((_: void) => api.staff.revokeSessions(id));

/** Own profile (Slack member id). The session identity is refetched. */
export function useUpdateOwnProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { slackUserId: string | null }) => api.session.updateProfile(v),
    onSuccess: async () => {
      await qc.fetchQuery({ queryKey: qk.session(), queryFn: () => api.session.current() });
      for (const key of [qk.staff.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** A one-time link to work a lead (or store, or organization) in the dealer app as yourself. */
export function useActLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { organizationId: string; storeId?: string; opportunityId?: string }) => api.opportunities.actLink(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.audit.all }),
  });
}

/** Own password. Afterwards the session is refetched (clears a temporary-password gate). */
export function useChangeOwnPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { currentPassword: string; newPassword: string }) => api.session.changePassword(v),
    onSuccess: async () => {
      await qc.fetchQuery({ queryKey: qk.session(), queryFn: () => api.session.current() });
      void qc.invalidateQueries({ queryKey: qk.audit.all });
    },
  });
}

// ── tenant provisioning ──────────────────────────────────────────────────
const TENANT_KEYS = [qk.organizations.all, qk.stores.all, qk.users.all, qk.audit.all];

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewOrganization) => api.organizations.create(input),
    onSuccess: (org) => {
      qc.setQueryData(qk.organizations.detail(org.id), org);
      for (const key of TENANT_KEYS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

function useStoreMutation<V>(fn: (v: V) => Promise<StoreDetail>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (store) => {
      qc.setQueryData(qk.stores.detail(store.id), store);
      for (const key of TENANT_KEYS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export const useCreateStore = (organizationId: string) => useStoreMutation((input: StoreInput) => api.stores.create(organizationId, input));
export const useUpdateStore = (storeId: string) => useStoreMutation((patch: Partial<StoreInput>) => api.stores.update(storeId, patch));
export const useUpdateStorePolicy = (storeId: string) => useStoreMutation((patch: StorePolicyPatch) => api.stores.updatePolicy(storeId, patch));
export const useDeactivateStore = (storeId: string) => useStoreMutation((reason: string) => api.stores.deactivate(storeId, reason));
// biome-ignore lint/suspicious/noConfusingVoidType: mutate() takes no variables
export const useActivateStore = (storeId: string) => useStoreMutation((_: void) => api.stores.activate(storeId));
export const useSetStoreMember = (storeId: string) =>
  useStoreMutation((v: { userId: string; role: StoreRole }) => api.stores.setMember(storeId, v.userId, v.role));
/** Remove one member from a store (from the store's page). */
export const useRemoveStoreMember = (storeId: string) =>
  useStoreMutation(async (userId: string) => {
    await api.users.removeMembership({ userId, storeId });
    return api.stores.get(storeId);
  });

function useDealerCredentialsMutation<V>(fn: (v: V) => Promise<DealerCredentials>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      qc.setQueryData(qk.users.detail(r.user.id), r.user);
      for (const key of TENANT_KEYS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export const useCreateDealerUser = (organizationId: string) => useDealerCredentialsMutation((input: NewDealerUser) => api.users.create(organizationId, input));
// biome-ignore lint/suspicious/noConfusingVoidType: mutate() takes no variables
export const useResetUserPassword = (userId: string) => useDealerCredentialsMutation((_: void) => api.users.resetPassword(userId));

export function useRemoveOrganizationMember(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.organizations.removeMember(organizationId, userId),
    onSuccess: () => {
      for (const key of TENANT_KEYS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** Development only: text the store as the seller (Twilio-shaped inbound messages). */
export function useSimulateInboundSms(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SimulateInboundInput) => api.dev.simulateInbound(conversationId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.conversations.messages(conversationId) });
      void qc.invalidateQueries({ queryKey: qk.conversations.detail(conversationId) });
      void qc.invalidateQueries({ queryKey: qk.conversations.timeline(conversationId) });
      void qc.invalidateQueries({ queryKey: qk.audit.all });
    },
  });
}

// ── Offer API integrations ─────────────────────────────────────────────────
function useOfferIntegrationMutation<V>(fn: (v: V) => Promise<OfferIntegrationDetail>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (detail) => {
      qc.setQueryData(qk.integrations.offerDetail(detail.id), detail);
      for (const key of [qk.integrations.all, qk.organizations.all, qk.stores.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}
export const useCreateOfferIntegration = () => useOfferIntegrationMutation((input: OfferIntegrationInput) => api.offerApi.create(input));
export const useUpdateOfferIntegration = (id: string) => useOfferIntegrationMutation((patch: OfferIntegrationPatch) => api.offerApi.update(id, patch));
/** Turn an offer integration on (with the store's external id) or off for a store. */
export const useLinkStoreOfferIntegration = (storeId: string) =>
  useStoreMutation((v: { integrationId: string; externalStoreId: string | null }) => api.offerApi.linkStore(storeId, v.integrationId, v.externalStoreId));
export const useUnlinkStoreOfferIntegration = (storeId: string) =>
  useStoreMutation((integrationId: string) => api.offerApi.unlinkStore(storeId, integrationId));
/** Send a failed or skipped CRM email (ADF lead) again. */
export function useRetryCrmEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (crmEmailId: string) => api.integrations.retryCrmEmail(crmEmailId),
    onSuccess: () => {
      for (const key of [qk.integrations.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}
/** Run the Offer API again for a failed run's lead; the new run shows in the log when it's done. */
export function useRetryOfferRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api.offerApi.retryRun(runId),
    onSuccess: () => {
      for (const key of [qk.integrations.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** Saves global (organizationId null) or an organization's own follow-up settings. */
export function useUpdateFollowupSettings(organizationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: FollowupSettingsValue) => api.followups.update(organizationId, settings),
    onSuccess: (view) => {
      qc.setQueryData(qk.prompts.followups(organizationId), view);
      // Organizations inheriting global see the change too.
      for (const key of [["prompts", "followups"], qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** Leads that went quiet before follow-ups: preview (dryRun) or plan their follow-ups. */
export function useCatchUpFollowups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FollowupCatchUpInput) => api.followups.catchUp(input),
    onSuccess: (r) => {
      if (!r.dryRun) void qc.invalidateQueries({ queryKey: qk.audit.all });
    },
  });
}

/** Drops this scope's own follow-up settings (an organization inherits global again). */
export function useResetFollowupSettings(organizationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.followups.reset(organizationId),
    onSuccess: (view) => {
      qc.setQueryData(qk.prompts.followups(organizationId), view);
      for (const key of [["prompts", "followups"], qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** Edits global (organizationId null) or an organization's prompt parts; null per key = inherit. */
export function useUpdatePromptSettings(organizationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sections: Record<string, string | null>) => api.prompts.update(organizationId, sections),
    onSuccess: (settings) => {
      qc.setQueryData(qk.prompts.settings(organizationId), settings);
      // An organization's effective text depends on global.
      for (const key of [qk.prompts.all, qk.audit.all]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}
